import React from 'react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import CallToAction from '../components/CallToAction';

const PortfolioItem = ({ title, industry, description, results, beforeImg, afterImg }) => {
  return (
    <div className="mb-20">
      <div className="grid md:grid-cols-2 gap-8 mb-6">
        <div className="relative">
          <div className="absolute -inset-0.5 bg-gradient-to-br from-secondary/30 to-neutral-dark/30 rounded-xl blur-sm opacity-30"></div>
          <div className="relative rounded-xl overflow-hidden shadow-soft">
            <img 
              src={beforeImg} 
              alt={`${title} before`}
              className="w-full aspect-video object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-secondary/80 text-white py-2 px-4">
              <p className="text-center font-medium">Before</p>
            </div>
          </div>
        </div>
        
        <div className="relative">
          <div className="absolute -inset-0.5 bg-gradient-to-br from-primary/30 to-accent/30 rounded-xl blur-sm opacity-30"></div>
          <div className="relative rounded-xl overflow-hidden shadow-soft">
            <img 
              src={afterImg} 
              alt={`${title} after`}
              className="w-full aspect-video object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-primary/80 text-white py-2 px-4">
              <p className="text-center font-medium">After</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="glass-card p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h3 className="text-2xl font-bold">{title}</h3>
          <span className="inline-block py-1 px-3 bg-primary/10 text-primary rounded-full text-sm font-medium">
            {industry}
          </span>
        </div>
        
        <p className="text-secondary-light mb-4">
          {description}
        </p>
        
        <div className="bg-neutral-light/50 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6 text-primary flex-shrink-0 mt-0.5">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            <div>
              <h4 className="font-semibold text-lg">Results</h4>
              <p>{results}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PortfolioPage = () => {
  // Portfolio data
  const portfolioItems = [
    {
      title: "ABC Plumbing",
      industry: "Plumbing",
      description: "Complete website redesign for a local plumbing company. We improved mobile responsiveness, optimized the site for conversion, and added clear call-to-action buttons throughout the site.",
      results: "50% increase in lead inquiries within the first month after launch.",
      beforeImg: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
      afterImg: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
    },
    {
      title: "Elite Electrical",
      industry: "Electrical",
      description: "Built a new modern website for an electrical contractor with service area pages, emergency service highlights, and integrated customer review system.",
      results: "3x increase in organic traffic and 75% more service requests.",
      beforeImg: "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
      afterImg: "https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
    },
    {
      title: "Pro HVAC Solutions",
      industry: "HVAC",
      description: "Redesigned an outdated website with a focus on seasonal services, emergency repairs, and maintenance plans. Added online quote request system.",
      results: "Doubled conversion rate and improved Google ranking from page 3 to page 1.",
      beforeImg: "https://images.unsplash.com/photo-1603302576837-37561b2e2302?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
      afterImg: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
    },
    {
      title: "Top Tier Roofing",
      industry: "Roofing",
      description: "Created a comprehensive website showcasing different roofing materials, repair services, and emergency storm damage response. Implemented before/after project gallery.",
      results: "40% increase in qualified leads and 25% higher average project value.",
      beforeImg: "https://images.unsplash.com/photo-1586282391535-74c5a43259d7?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
      afterImg: "https://images.unsplash.com/photo-1611095973763-414019e72400?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
    },
    {
      title: "Green Scapes Landscaping",
      industry: "Landscaping",
      description: "Developed a visually engaging website highlighting landscape design, maintenance services, and outdoor living projects. Added seasonal service scheduler.",
      results: "60% increase in spring bookings and expanded service area by 15 miles.",
      beforeImg: "https://images.unsplash.com/photo-1517077304055-6e89abbf09b0?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
      afterImg: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
    },
    {
      title: "Quality Construction",
      industry: "General Contracting",
      description: "Built a modern website featuring project categories, detailed service descriptions, and project estimation tools. Implemented customer testimonial showcase.",
      results: "Increased quality of leads and 35% higher website engagement metrics.",
      beforeImg: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
      afterImg: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
    },
    {
      title: "Fast Plumbers Inc",
      industry: "Plumbing",
      description: "Redesigned a poorly performing website with focus on emergency services, clear pricing, and online booking. Added service area maps and technician profiles.",
      results: "100% increase in emergency service calls and 45% reduction in booking abandonment.",
      beforeImg: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
      afterImg: "https://images.unsplash.com/photo-1591696205602-2f950c417cb9?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
    },
    {
      title: "Johnson Air & Heat",
      industry: "HVAC",
      description: "Created a comprehensive website featuring maintenance plans, troubleshooting guides, and emergency service options. Implemented online scheduling system.",
      results: "300% more maintenance plan sign-ups and improved customer retention.",
      beforeImg: "https://images.unsplash.com/photo-1492168732976-2676c584c675?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
      afterImg: "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
    }
  ];

  return (
    <>
      <SEO
        title="Portfolio | Snap Launch Studio Website Examples"
        description="View examples of websites we've built for contractors and service businesses. See real results from our website development and design work."
        keywords="website portfolio, contractor websites, service business web design, web development examples, before and after websites"
        canonicalUrl="https://www.snaplaunchstudio.com/portfolio"
      />
      <PageHeader 
        title="Real Websites, Real Results" 
        subtitle="See actual websites we've built for contractors and small businesses"
      />
      
      <section className="py-16 bg-white">
        <div className="container">
          {portfolioItems.map((item, index) => (
            <PortfolioItem 
              key={index}
              title={item.title}
              industry={item.industry}
              description={item.description}
              results={item.results}
              beforeImg={item.beforeImg}
              afterImg={item.afterImg}
            />
          ))}
        </div>
      </section>
      
      <CallToAction title="Want a website like these? Let's talk" />
    </>
  );
};

export default PortfolioPage;
